const normalizePath = (path: string): string => {
  if (path === '/') return path
  return path.replace(/\/$/, '')
}

const normalizeSeoText = (value: unknown): string => {
  if (typeof value !== 'string') return ''
  const normalized = value.trim()
  return normalized === 'null' || normalized === 'undefined' ? '' : normalized
}

export const useCustomSeo = (data: {
  title?: string | (() => string | undefined)
  description?: string | (() => string | undefined)
  image?: string | (() => string | undefined)
  keywords?: string | (() => string | undefined)
  noindex?: boolean | (() => boolean)
}) => {
  const { $i18n } = useNuxtApp()
  const websiteInfoStore = useWebsiteInfoStore()
  const route = useRoute()
  const { domain } = useNavigationMenu()
  const { alternates } = useHreflang()

  const normalizedPath = computed(() => normalizePath(route.path))
  const fullURL = computed(() => {
    const query = route.query && Object.keys(route.query).length
      ? `?${new URLSearchParams(route.query as Record<string, string>).toString()}`
      : ''
    return `${domain.value}${normalizedPath.value}${query}`
  })

  const description = computed(() => {
    const raw = typeof data?.description === 'function' ? data.description() : data?.description
    return normalizeSeoText(raw) || normalizeSeoText(websiteInfoStore.description)
  })

  const title = computed(() => normalizeSeoText(
    typeof data?.title === 'function' ? data.title() : data?.title,
  ) || normalizeSeoText($i18n.t('global.loading')))
  const image = computed(() => normalizeSeoText(
    typeof data?.image === 'function' ? data.image() : data?.image,
  ) || normalizeSeoText(websiteInfoStore?.favicon?.small))
  const favicon = computed(() => normalizeSeoText(websiteInfoStore?.favicon?.small))
  const keywords = computed(() => normalizeSeoText(
    typeof data?.keywords === 'function' ? data.keywords() : data?.keywords,
  ))

  useHead(() => ({
    title: title.value,
    htmlAttrs: {
      lang: $i18n.locale.value,
    },
    link: [
      {
        rel: 'shortcut icon',
        type: 'image/x-icon',
        href: favicon.value,
      },
      {
        rel: 'canonical',
        href: fullURL.value,
      },
      ...alternates.value,
    ],
    titleTemplate: '%s',
  }))

  useSeoMeta({
    robots: () => {
      if (websiteInfoStore.robotDisabled) return 'noindex, nofollow'
      const shouldNoindex = typeof data?.noindex === 'function' ? data.noindex() : data?.noindex
      if (shouldNoindex) return 'noindex, follow'
      return 'index, follow'
    },
    title: title.value,
    description: () => description.value,
    keywords: keywords.value,

    // Open Graph
    ogTitle: title.value,
    ogDescription: () => description.value,
    ogUrl: fullURL.value,
    ogSiteName: websiteInfoStore.name,
    ogImage: image.value,
    // 'ogImage:secureUrl': data?.image || '',

    // Twitter
    twitterTitle: title.value,
    twitterDescription: () => description.value,
    twitterImage: image.value,
    twitterCard: 'summary',

    // Apple specific
    appleMobileWebAppTitle: title.value,
  })
}
